import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { badgesApi } from '../../services/badgesApi';
import { TIERS, TIER_ORDER, TIER_KEYS, STYLE_NAMES, buildSVG, jsonToConfig } from '../../utils/badgeForge';
import BadgeShowcase from './BadgeShowcase';
import './BadgePrizeShowcase.css';

const renderBadgeSvg = (tpl, size) => {
  try {
    const cfg = tpl.svgConfig ? jsonToConfig(tpl.svgConfig) : {
      style: tpl.style || tpl.badgeTemplateStyle || 'crest',
      tier: tpl.tier || tpl.badgeTemplateTier || 'common',
      iconIndex: tpl.iconIndex ?? tpl.badgeTemplateIconIndex ?? 0,
      iconScale: tpl.iconScale ?? tpl.badgeTemplateIconScale ?? 0.26,
    };
    return buildSVG(
      cfg.style || tpl.style || tpl.badgeTemplateStyle || 'crest',
      cfg.tier || tpl.tier || tpl.badgeTemplateTier || 'common',
      cfg.iconIndex ?? tpl.iconIndex ?? tpl.badgeTemplateIconIndex ?? 0,
      size,
      cfg.iconScale ?? tpl.iconScale ?? tpl.badgeTemplateIconScale ?? 0.26
    );
  } catch {
    return null;
  }
};

const getTierInfo = (tpl) => {
  const tier = tpl.tier || tpl.badgeTemplateTier || 'common';
  return TIERS[tier] || TIERS.common;
};

const BadgePrizeItem = ({ template, rankLabel, index, onShowcase }) => {
  const tierInfo = getTierInfo(template);
  const svg = useMemo(() => renderBadgeSvg(template, 120), [template]);
  const delay = index * 0.12;

  if (!svg) return null;

  const fakeUserBadge = {
    badgeTemplate: {
      name: template.name || template.badgeTemplateName,
      description: template.description || template.badgeTemplateDescription,
      style: template.style || template.badgeTemplateStyle,
      tier: template.tier || template.badgeTemplateTier,
      iconIndex: template.iconIndex ?? template.badgeTemplateIconIndex,
      iconScale: template.iconScale ?? template.badgeTemplateIconScale,
      svgConfig: template.svgConfig || template.badgeTemplateSvgConfig,
    },
    awardedAt: null,
    awardReason: rankLabel ? `Prize for ${rankLabel}` : 'Prize badge',
  };

  return (
    <div
      className="bps__item"
      style={{ '--gc': tierInfo.color, '--gg': tierInfo.glow, animationDelay: `${delay}s` }}
      onClick={() => onShowcase(fakeUserBadge)}
    >
      <div className="bps__item-glow" />
      {rankLabel && <div className="bps__item-rank">{rankLabel}</div>}
      <div
        className="bps__item-svg"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      <div className="bps__item-name">{template.name || template.badgeTemplateName || 'Unnamed'}</div>
      <div className="bps__item-tier" style={{ color: tierInfo.color }}>
        {tierInfo.name}
      </div>
    </div>
  );
};

const BadgePrizeShowcase = ({ seriesIds, badgeAwards, title, subtitle, compact = false }) => {
  const [seriesData, setSeriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showcase, setShowcase] = useState(null);

  const handleShowcase = useCallback((ub) => setShowcase(ub), []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const results = [];

        if (badgeAwards && badgeAwards.length > 0) {
          const grouped = {};
          badgeAwards.forEach(award => {
            const sid = award.badgeSeriesId || 'unsorted';
            if (!grouped[sid]) grouped[sid] = { seriesName: award.badgeSeriesName || 'Individual Badges', badges: [] };
            const rankLabel = award.minRank === award.maxRank
              ? `Rank #${award.minRank}`
              : `Ranks #${award.minRank}–${award.maxRank}`;
            grouped[sid].badges.push({ ...award, _rankLabel: rankLabel });
          });
          Object.values(grouped).forEach(g => {
            g.badges.sort((a, b) => (a.minRank || 0) - (b.minRank || 0));
            results.push(g);
          });
        }

        if (seriesIds && seriesIds.length > 0) {
          for (const sid of seriesIds) {
            try {
              const series = await badgesApi.getPublicSeries(sid);
              const templates = await badgesApi.getPublicSeriesTemplates(sid);
              results.push({
                seriesName: series.name || `Series ${sid}`,
                seriesDescription: series.description,
                badges: templates.map(t => ({ ...t, _rankLabel: null })),
              });
            } catch (err) {
              // Skip series that can't be loaded
            }
          }
        }

        if (active) {
          setSeriesData(results);
          setLoading(false);
        }
      } catch (err) {
        if (active) {
          setError('Failed to load prize badges');
          setLoading(false);
        }
      }
    })();
    return () => { active = false; };
  }, [seriesIds, badgeAwards]);

  if (loading) {
    return (
      <div className="bps__loading">
        <div className="bps__loading-spinner" />
        <span>Loading prizes...</span>
      </div>
    );
  }

  if (error) {
    return <div className="bps__error">{error}</div>;
  }

  if (seriesData.length === 0) {
    return null;
  }

  return (
    <div className={`bps__container${compact ? ' bps__container--compact' : ''}`}>
      {(title || subtitle) && (
        <div className="bps__header">
          {title && <h3 className="bps__title">{title}</h3>}
          {subtitle && <p className="bps__subtitle">{subtitle}</p>}
        </div>
      )}

      <div className="bps__aurora" />

      {seriesData.map((series, si) => (
        <div key={si} className="bps__series">
          <div className="bps__series-header">
            <div className="bps__series-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
            <div>
              <h4 className="bps__series-name">{series.seriesName}</h4>
              {series.seriesDescription && (
                <p className="bps__series-desc">{series.seriesDescription}</p>
              )}
            </div>
          </div>

          <div className="bps__badges-row">
            {series.badges.map((badge, bi) => (
              <BadgePrizeItem
                key={badge.id || badge.badgeTemplateId || bi}
                template={badge}
                rankLabel={badge._rankLabel}
                index={bi}
                onShowcase={handleShowcase}
              />
            ))}
          </div>
        </div>
      ))}

      {showcase && (
        <BadgeShowcase userBadge={showcase} onClose={() => setShowcase(null)} />
      )}
    </div>
  );
};

export default BadgePrizeShowcase;
